import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputTextarea } from "primereact/inputtextarea";
import { InputNumber } from "primereact/inputnumber";
import { ConfirmDialog } from "../components/shared/ConfirmDialog";
import { AsociarProductosDialog } from "../components/dialogs/AsociarProductosDialog";
import { FotosProductoDialog } from "../components/dialogs/FotosProductoDialog";
import { FichaProductoDialog } from "../components/dialogs/FichaProductoDialog";
import AppToast from "../../../shared/components/AppToast";
import { ensureCatalogAuth } from "../../../services/backend/authService";
import { getProductosByFamilia, desasociarProducto, grabarAtributoProducto, } from "../../../services/backend/productoService";
import { getAtributosFamilia, getValoresAtributo, getFamiliasAll, } from "../../../services/backend/catalogService";
import logger from "../../../shared/utils/logger";

const ProductosFamilia = () => {
  const { familiaId: familiaIdParam } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const toastRef = useRef(null);

  const familiaId = Number(familiaIdParam) || 0;
  const tipo = searchParams.get("tipo") || "B2B";
  const nombreFromQuery = searchParams.get("nombre") || "";

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [visibleConfirm, setVisibleConfirm] = useState(false);
  const [visibleAsociar, setVisibleAsociar] = useState(false);
  const [visibleFotos, setVisibleFotos] = useState(false);
  const [productoFotos, setProductoFotos] = useState(null);
  const [visibleFicha, setVisibleFicha] = useState(false);
  const [productoFicha, setProductoFicha] = useState(null);
  const [atributos, setAtributos] = useState([]);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);
  const [familiaName, setFamiliaName] = useState(nombreFromQuery);

  const familia = { id: familiaId, name: familiaName, nombre: familiaName };

  const fetchAtributos = useCallback(async () => {
    if (!familiaId) {
      setAtributos([]);
      return;
    }
    try {
      await ensureCatalogAuth();
      const response = await getAtributosFamilia(familiaId);
      const items = Array.isArray(response?.atributos)
        ? response.atributos
        : Array.isArray(response)
        ? response
        : [];
      const conValores = await Promise.all(
        items.map(async (attr) => {
          try {
            const valRes = await getValoresAtributo(attr.id);
            const valores = Array.isArray(valRes?.valores)
              ? valRes.valores
              : Array.isArray(valRes)
              ? valRes
              : [];
            return { ...attr, valores };
          } catch {
            return { ...attr, valores: [] };
          }
        })
      );
      setAtributos(conValores);
    } catch (err) {
      logger.error("Error al cargar atributos:", err);
      setAtributos([]);
    }
  }, [familiaId]);

  const fetchProducts = useCallback(async () => {
    if (!familiaId) {
      setProducts([]);
      return;
    }
    setLoading(true);
    try {
      await ensureCatalogAuth();
      await fetchAtributos();
      const response = await getProductosByFamilia(familiaId);
      const items = Array.isArray(response)
        ? response
        : Array.isArray(response?.productos)
        ? response.productos
        : [];
      setProducts(items);
    } catch (err) {
      logger.error("Error al cargar productos de familia:", err);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [familiaId, fetchAtributos]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    if (!familiaId || familiaName) return;
    let mounted = true;
    const resolveNombre = async () => {
      try {
        await ensureCatalogAuth();
        const response = await getFamiliasAll({ tipo });
        const items = Array.isArray(response?.familias)
          ? response.familias
          : Array.isArray(response)
          ? response
          : [];
        const found = findFamiliaById(items, familiaId);
        if (mounted && found?.nombre) setFamiliaName(found.nombre);
      } catch (err) {
        logger.error("Error al resolver nombre de familia:", err);
      }
    };
    resolveNombre();
    return () => {
      mounted = false;
    };
  }, [familiaId, familiaName, tipo]);

  const handleDesasociar = async () => {
    setLoading(true);
    try {
      await ensureCatalogAuth();
      for (const prod of selectedProducts) {
        await desasociarProducto({
          codigo: prod.codigo,
          familia: familiaId,
          tipo: tipo || "B2B",
        });
      }
      setSelectedProducts([]);
      setVisibleConfirm(false);
      await fetchProducts();
      toastRef.current?.show({
        severity: "success",
        summary: "Desasociado",
        detail: "Productos desasociados correctamente.",
        life: 3000,
      });
    } catch (err) {
      logger.error("Error al desasociar productos:", err);
      toastRef.current?.show({
        severity: "error",
        summary: "Error",
        detail: "No se pudieron desasociar los productos.",
        life: 4000,
      });
    } finally {
      setLoading(false);
    }
  };

  const getSelectedValue = (producto, atributoId) => {
    const fromEdits = edits[producto.codigo]?.[atributoId];
    if (fromEdits !== undefined) return fromEdits;
    let raw = producto.atributo;
    if (typeof raw === "string") {
      try {
        raw = JSON.parse(raw);
      } catch {
        raw = {};
      }
    }
    const original = raw?.[String(atributoId)];
    return Array.isArray(original) ? original[0] ?? null : original ?? null;
  };

  const handleAtributoChange = (producto, atributoId, valorId) => {
    setEdits((prev) => ({
      ...prev,
      [producto.codigo]: {
        ...prev[producto.codigo],
        [atributoId]: valorId,
      },
    }));
  };

  const handleFieldChange = (codigo, campo, valor) => {
    setEdits((prev) => ({
      ...prev,
      [codigo]: {
        ...prev[codigo],
        [campo]: valor,
      },
    }));
  };

  const handleGuardar = async () => {
    setSaving(true);
    try {
      await ensureCatalogAuth();
      for (const prod of products) {
        const productEdits = edits[prod.codigo] || {};
        const atributosFinal = {};
        for (const attr of atributos) {
          const val = getSelectedValue(prod, attr.id);
          if (val !== null && val !== undefined) {
            atributosFinal[attr.id] = [val];
          }
        }
        await grabarAtributoProducto({
          codigo: prod.codigo,
          atributo: JSON.stringify(atributosFinal),
          descripcion:
            productEdits.descripcionAtributo ?? prod.descripcionAtributo ?? "",
          aplicacion: productEdits.aplicacion ?? prod.aplicacion ?? "",
          conversion: productEdits.conversion ?? prod.conversion ?? 1,
        });
      }
      setEdits({});
      toastRef.current?.show({
        severity: "success",
        summary: "Guardado",
        detail: "Los cambios se guardaron correctamente.",
        life: 3000,
      });
      await fetchProducts();
    } catch (err) {
      logger.error("Error al guardar atributos de productos:", err);
      toastRef.current?.show({
        severity: "error",
        summary: "Error",
        detail: "No se pudieron guardar los cambios.",
        life: 4000,
      });
    } finally {
      setSaving(false);
    }
  };

  const descripcionAtributoBody = (rowData) => (
    <div className="p-fluid">
      <InputTextarea
        value={
          edits[rowData.codigo]?.descripcionAtributo ??
          rowData.descripcionAtributo ??
          ""
        }
        onChange={(e) =>
          handleFieldChange(
            rowData.codigo,
            "descripcionAtributo",
            e.target.value
          )
        }
        className="w-full"
        maxLength={200}
      />
    </div>
  );

  const aplicacionBody = (rowData) => (
    <div className="p-fluid">
      <InputTextarea
        value={edits[rowData.codigo]?.aplicacion ?? rowData.aplicacion ?? ""}
        onChange={(e) =>
          handleFieldChange(rowData.codigo, "aplicacion", e.target.value)
        }
        rows={3}
        className="w-full"
        maxLength={200}
      />
    </div>
  );

  const conversionBody = (rowData) => (
    <div className="p-fluid">
      <InputNumber
        value={edits[rowData.codigo]?.conversion ?? rowData.conversion ?? 1}
        onValueChange={(e) =>
          handleFieldChange(rowData.codigo, "conversion", e.value)
        }
        mode="decimal"
        min={0.0001}
        max={1000000}
      />
    </div>
  );

  const atributosBody = (rowData) => (
    <div className="p-fluid">
      {atributos.map((attr) => (
        <div key={attr.id}>
          <label className="text-xs text-500 mb-1">{attr.llave}</label>
          <Dropdown
            value={getSelectedValue(rowData, attr.id)}
            options={attr.valores.map((v) => ({
              label: v.valor || v.nombre,
              value: v.id,
            }))}
            onChange={(e) => handleAtributoChange(rowData, attr.id, e.value)}
            placeholder="Seleccionar"
            className="p-inputtext-sm"
          />
        </div>
      ))}
    </div>
  );

  const accionesBody = (rowData) => {
    return (
      <>
        <Button
          icon="pi pi-images"
          size="medium"
          outlined
          severity="info"
          tooltip="Ver imágenes"
          tooltipOptions={{ position: "left" }}
          label="Ver"
          onClick={() => {
            setProductoFotos(rowData);
            setVisibleFotos(true);
          }}
        />
   
        <Button
          className="mt-2"
          icon="pi pi-file-pdf"
          size="medium"
          outlined
          severity="secondary"
          tooltip="Ficha técnica"
          tooltipOptions={{ position: "left" }}
          label="PDF"
          onClick={() => {
            setProductoFicha(rowData);
            setVisibleFicha(true);
          }}
        />
      </>
      
    );
  };

  const header = (
    <div className="flex align-items-center justify-content-between flex-wrap gap-2">
      <span className="text-sm font-semibold">Familia: {familiaName}</span>
      <div className="flex gap-2">
        <Button
          icon="pi pi-save"
          label="Guardar"
          onClick={handleGuardar}
          disabled={saving}
          loading={saving}
          severity="success"
        />
        <Button
          icon="pi pi-link"
          label="Asociar Producto(s)"
          onClick={() => setVisibleAsociar(true)}
          severity="primary"
        />
        <Button
          icon="pi pi-times"
          label="Desasociar Producto(s)"
          onClick={() => setVisibleConfirm(true)}
          disabled={!selectedProducts.length}
          severity="danger"
        />
      </div>
    </div>
  );

  return (
    <div className="py-3">
      <div className="flex align-items-center gap-2 mb-3">
        <Button
          icon="pi pi-arrow-left"
          label="Volver a Familias"
          className="p-button-text"
          onClick={() => navigate("/administracion/familias")}
        />
      </div>

      <DataTable
        value={products}
        header={header}
        showGridlines
        paginator
        rows={10}
        dataKey="codigo"
        stripedRows
        filter
        scrollable
        scrollHeight="50vh"
        loading={loading}
        rowsPerPageOptions={[10, 25, 50, 100]}
        tableStyle={{ width: "100%", tableLayout: "fixed" }}
        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
        currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} productos"
        selection={selectedProducts}
        onSelectionChange={(e) => setSelectedProducts(e.value)}
        selectionMode="multiple"
      >
        <Column selectionMode="multiple" headerStyle={{ width: "3%" }} />
        <Column
          header="Código"
          field="codigo"
          sortable
          style={{ width: "8%" }}
        />
        <Column
          header="Descripción"
          field="descripcion"
          sortable
          style={{ width: "17%" }}
        />
    
        <Column
          header="Atributos"
          body={atributosBody}
          style={{ width: "15%" }}
        />
        <Column
          header="Título"
          body={descripcionAtributoBody}
          style={{ width: "22%" }}
        />
        <Column
          header="Aplicación"
          body={aplicacionBody}
          style={{ width: "21%" }}
        />

        <Column header="Und." field="unidad" style={{ width: "5%" }} />

        <Column
          header="Conv."
          body={conversionBody}
          style={{ width: "7%" }}
        />

        <Column header="Acciones" body={accionesBody} style={{ width: "8%" }} />
      </DataTable>

      <ConfirmDialog
        visible={visibleConfirm}
        onCancel={() => setVisibleConfirm(false)}
        onConfirm={handleDesasociar}
        header="Desasociar Producto(s)"
        message={`¿Desasociar ${selectedProducts.length} producto(s) de esta familia?`}
        loading={loading}
      />

      <AsociarProductosDialog
        visible={visibleAsociar}
        onHide={() => setVisibleAsociar(false)}
        familia={familia}
        tipo={tipo}
        productosAsociados={products}
        onAsociado={fetchProducts}
      />

      <FotosProductoDialog
        visible={visibleFotos}
        onHide={() => setVisibleFotos(false)}
        producto={productoFotos}
      />

      <FichaProductoDialog
        visible={visibleFicha}
        onHide={() => setVisibleFicha(false)}
        producto={productoFicha}
      />

      <AppToast ref={toastRef} />
    </div>
  );
};

const findFamiliaById = (familias, id) => {
  for (const f of familias) {
    if (f.id === id) return f;
    if (Array.isArray(f.hijos) && f.hijos.length) {
      const found = findFamiliaById(f.hijos, id);
      if (found) return found;
    }
  }
  return null;
};

export default ProductosFamilia;
